import * as React from "react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import useAbortJob from "./useAbortJob";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useNetworkStatus } from "./connectionsTest";

// Why the job was aborted, for the tracker's aborted list (D10). Only customer refusals
// carried a reason before, so an installer's abort never appeared there.
export const ABORT_REASONS = [
  "Customer not home",
  "Customer changed their mind",
  "Site unsafe or not ready",
  "Device faulty",
  "Wrong address or job",
  "Other",
];

// The reason to send, or "" while none is chosen (or Other has no text yet).
export function abortReasonText(choice, other) {
  if (choice !== "Other") return choice;
  return other.trim() ? `Other: ${other.trim()}` : "";
}

export function AbortReasonPicker({ choice, other, onChoice, onOther }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.75)", mb: 1 }}>
        Why are you aborting?
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {ABORT_REASONS.map((reason) => (
          <Box
            key={reason}
            component="button"
            type="button"
            onClick={() => onChoice(reason)}
            sx={{
              px: "12px", py: "8px", borderRadius: "8px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600,
              border: `1px solid ${choice === reason ? "#ef4444" : "rgba(255,255,255,0.15)"}`,
              backgroundColor: choice === reason ? "rgba(239,68,68,0.15)" : "transparent",
              color: choice === reason ? "#fff" : "rgba(255,255,255,0.7)",
            }}
          >
            {reason}
          </Box>
        ))}
      </Box>
      {choice === "Other" && (
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="What happened?"
          value={other}
          onChange={(e) => onOther(e.target.value)}
          inputProps={{ maxLength: 200 }}
          sx={{
            mt: 1.5,
            "& .MuiOutlinedInput-root": { color: "#fff", "& fieldset": { borderColor: "rgba(255,255,255,0.2)" } },
          }}
        />
      )}
    </Box>
  );
}

// Shown in an abort dialog while offline. Confirm used to look ready and only then say signal
// was needed (stress test 26/10, P2); now the dialog says so up front and Confirm waits.
export function OfflineAbortNote({ online }) {
  if (online) return null;
  return (
    <Box sx={{ mt: 2, p: "10px 12px", borderRadius: "8px", backgroundColor: "rgba(253,185,64,0.08)", border: "1px solid rgba(253,185,64,0.35)" }}>
      <Typography sx={{ fontSize: "0.82rem", color: "#FDB940", fontWeight: 600 }}>
        You need signal to abort. Confirm works again once you're reconnected.
      </Typography>
    </Box>
  );
}

const AbortJobButton = React.forwardRef(function AbortJobButton(props, ref) {
  const { job_card_reference_id } = useParams();
  const [open, setOpen] = useState(false);
  React.useImperativeHandle(ref, () => ({ openDialog: () => setOpen(true) }));
  const [choice, setChoice] = useState("");
  const [other, setOther] = useState("");
  const { abort, isRedo, installState } = useAbortJob(job_card_reference_id);
  const online = useNetworkStatus();
  const jobNoun = { UN: "uninstall", RE: "replacement" }[installState] || "installation";
  const reason = abortReasonText(choice, other);

  const handleConfirm = () => abort(isRedo ? "" : reason);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        disableElevation
        sx={{
          flex: 1,
          py: "13px",
          borderRadius: "10px",
          backgroundColor: "#0d1117",
          color: "#e0e6f0",
          fontSize: "1rem",
          fontWeight: 600,
          textTransform: "none",
          border: "1px solid rgba(255,255,255,0.12)",
          "&:hover": { backgroundColor: "#161b22" },
        }}
      >
        Abort
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1A1C24",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.07)",
            mx: 2,
          },
        }}
      >
        <DialogContent>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", mb: 1 }}>
            {isRedo ? "Stop this redo?" : `Abort this ${jobNoun}?`}
          </Typography>
          <DialogContentText sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.55)" }}>
            {isRedo
              ? "The job goes back to Alerts as a redo. When you start it again you'll begin from the before photos, and the client details are kept."
              : "The job card will be marked Aborted. This cannot be undone. You'll be returned to the dashboard, where you can start a new job."}
          </DialogContentText>
          {!isRedo && <AbortReasonPicker choice={choice} other={other} onChoice={setChoice} onOther={setOther} />}
          <OfflineAbortNote online={online} />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setOpen(false)}
            fullWidth
            sx={{
              backgroundColor: "rgba(255,255,255,0.06)",
              color: "#fff",
              borderRadius: "10px",
              fontWeight: 700,
              py: 1.2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            Back
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!online || (!isRedo && !reason)}
            fullWidth
            sx={{
              backgroundColor: "#ef4444",
              color: "#fff",
              borderRadius: "10px",
              fontWeight: 700,
              py: 1.2,
              "&:hover": { backgroundColor: "#dc2626" },
              "&.Mui-disabled": { backgroundColor: "rgba(239,68,68,0.3)", color: "rgba(255,255,255,0.5)" },
            }}
          >
            Confirm Abort
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default AbortJobButton;
